import type React from 'react';
import { useState } from 'react';

import { HumanResourcesItem, HumanResourcesResponse } from '@/app/models/detailsRH';
import { useUndoUnionDetailsHumanResourcesMutation } from '@/app/services/detailsHRApi';
import ExpandableScrollDataTable, { Column } from '@/components/common/ExpandableScrollDataTable';
import { usePeriod } from '@/context/PeriodContext';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import EditIcon from '@mui/icons-material/Edit';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Box, Checkbox, Menu, MenuItem, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import HumanResourcesDetailTable from './HumanResourcesDetailTable';

interface HumanResourcesTableProps {
  selectHumanResource: HumanResourcesItem[];
  setSelectHumanResource: (id: HumanResourcesItem[]) => void;
  setDrawerEdit: CallableFunction;
  setDrawerTransfer: CallableFunction;
  setDrawerEditRatio: CallableFunction;
  setSelectedHumanResourceItem: (id: HumanResourcesItem) => void;
  selectHumanResourceItem: HumanResourcesItem;
  humanResourceData: HumanResourcesResponse | undefined;
  setEditValues: CallableFunction;
}

export default function HumanResourcesTable({
  selectHumanResource,
  setSelectHumanResource,
  setDrawerEdit,
  setDrawerTransfer,
  setDrawerEditRatio,
  setSelectedHumanResourceItem,
  selectHumanResourceItem,
  humanResourceData,
  setEditValues,
}: HumanResourcesTableProps) {
  const theme = useTheme();
  const period = usePeriod();

  const [selectedStatus, setSelectedStatus] = useState<number | null>(null);
  const [idUndoUnion, setIdUndoUnion] = useState<number | null>(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { t } = useTranslation(['humanResource', 'common']);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = humanResourceData?.items;
      setSelectHumanResource(allIds as HumanResourcesItem[]);
    } else {
      setSelectHumanResource([]);
    }
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDrowersEdit = () => {
    setDrawerEdit(true);
    setAnchorEl(null);
  };

  const handleDrowersTrasnfer = () => {
    setDrawerTransfer(true);
    setAnchorEl(null);
  };

  const handleDrowersEditValues = () => {
    setEditValues(true);
    setAnchorEl(null);
  };
  const handleDrawerEditRatio = () => {
    setDrawerEditRatio(true);
    setAnchorEl(null);
  };

  const [undoUnion] = useUndoUnionDetailsHumanResourcesMutation();

  const handleUndoUnion = async () => {
    await toast.promise(
      undoUnion({
        id: idUndoUnion as number,
        period: period.selectedPeriod?.date as string,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:undo.union.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setDrawerEdit(false);
    setAnchorEl(null);
  };

  const columns: Column[] = [
    {
      key: 'select',
      label: (
        <Checkbox
          color="secondary"
          sx={{ p: 0 }}
          indeterminate={
            selectHumanResource.length > 0 && selectHumanResource.length < (selectHumanResource?.length as number)
          }
          checked={
            (humanResourceData?.items?.length as number) > 0 &&
            selectHumanResource?.length === humanResourceData?.items?.length
          }
          onChange={handleSelectAll}
          inputProps={{ 'aria-label': 'select all collaborators' }}
        />
      ),
      width: theme.spacing(7),
      sticky: 'left',
      stickyOffset: '0',
    },
    {
      key: 'name',
      label: t('common:name'),
      width: theme.spacing(40),
      sticky: 'left',
      stickyOffset: theme.spacing(7),
    },
    { key: 'cpf', label: t('cpf'), width: theme.spacing(25) },
    { key: 'position', label: t('position'), width: theme.spacing(20) },
    { key: 'graduationCourse', label: t('graduation.course') },
    { key: 'graduationLevel', label: t('graduation.level'), width: theme.spacing(20) },
    { key: 'hoursWorked', label: t('hours.worked'), width: theme.spacing(25) },
    { key: 'sharing', label: t('common:sharing'), align: 'center' },
    { key: 'extraHours', label: t('extra.hours'), width: theme.spacing(25) },
    {
      key: 'salary',
      label: t('salary'),
      width: theme.spacing(25),
      extraHeaderContent: {
        systemValue: humanResourceData?.header?.totalSalaryApp || 0,
        erpValue: humanResourceData?.header?.totalSalaryERP || 0,
        diffValue: (humanResourceData?.header?.totalSalaryApp || 0) - (humanResourceData?.header?.totalSalaryERP || 0),
      },
    },
    {
      key: 'awards',
      label: t('awards'),
      width: theme.spacing(25),
      extraHeaderContent: {
        systemValue: humanResourceData?.header?.totalAwardsApp || 0,
        erpValue: humanResourceData?.header?.totalAwardsERP || 0,
        diffValue: (humanResourceData?.header?.totalAwardsApp || 0) - (humanResourceData?.header?.totalAwardsERP || 0),
      },
    },
    {
      key: 'benefits',
      label: t('benefits'),
      width: theme.spacing(25),
      extraHeaderContent: {
        systemValue: humanResourceData?.header?.totalBenefitsApp || 0,
        erpValue: humanResourceData?.header?.totalBenefitsERP || 0,
        diffValue:
          (humanResourceData?.header?.totalBenefitsApp || 0) - (humanResourceData?.header?.totalBenefitsERP || 0),
      },
    },
    {
      key: 'charges',
      label: t('charges'),
      width: theme.spacing(25),
      extraHeaderContent: {
        systemValue: humanResourceData?.header?.totalChargesApp || 0,
        erpValue: humanResourceData?.header?.totalChargesERP || 0,
        diffValue:
          (humanResourceData?.header?.totalChargesApp || 0) - (humanResourceData?.header?.totalChargesERP || 0),
      },
    },
    {
      key: 'chargesAward',
      label: t('charges.award'),
      width: theme.spacing(25),
      extraHeaderContent: {
        systemValue: humanResourceData?.header?.totalChargesAwardApp || 0,
        erpValue: humanResourceData?.header?.totalChargesAwardERP || 0,
        diffValue:
          (humanResourceData?.header?.totalChargesAwardApp || 0) -
          (humanResourceData?.header?.totalChargesAwardERP || 0),
      },
    },
    { key: 'consolidated', label: t('common:consolidated'), align: 'center' },
    { key: 'grouped', label: t('common:grouped'), align: 'center' },
    { key: 'actions', label: t('common:actions'), width: theme.spacing(6), sticky: 'right', stickyOffset: '0' },
  ];

  // For expandable rows, we need a special renderRow function that returns the OverheadCollaboratorTable component
  const renderRow = (row: HumanResourcesItem) => (
    <HumanResourcesDetailTable
      row={row}
      selectHumanResource={selectHumanResource}
      setSelectHumanResource={setSelectHumanResource}
      setAnchorEl={setAnchorEl}
      setSelectedStatus={setSelectedStatus}
      setIdUndoUnion={setIdUndoUnion}
      setSelectedHumanResourceItem={setSelectedHumanResourceItem}
    />
  );

  // Use badge as the unique key.
  const getRowKey = (project: HumanResourcesItem, index: number) => project.id || `row-${index}`;

  return (
    <>
      <ExpandableScrollDataTable
        columns={columns}
        data={humanResourceData?.items || []}
        renderRow={renderRow}
        getRowKey={getRowKey}
        emptyMessage="Nenhum conteúdo encontrado"
        maxHeight="34rem"
        stickyHeader
        width={theme.spacing(204)}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedStatus && (
          <>
            <MenuItem onClick={() => handleDrowersEdit()}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ListAltIcon />
                {t('common:edit.detail')}
              </Box>
            </MenuItem>
            <MenuItem onClick={() => handleDrowersTrasnfer()}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompareArrowsIcon />
                {t('common:transfer')}
              </Box>
            </MenuItem>
            {selectHumanResourceItem.grouped && (
              <MenuItem onClick={() => handleUndoUnion()}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CallSplitIcon />
                  {t('common:undo.union')}
                </Box>
              </MenuItem>
            )}
            <MenuItem onClick={() => handleDrowersEditValues()}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EditIcon fontSize="small" />
                {t('edit.values')}
              </Box>
            </MenuItem>
            <MenuItem onClick={() => handleDrawerEditRatio()}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CurrencyExchangeIcon />
                {t('edit.ratio')}
              </Box>
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
}
